import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MagnifyingGlass, Users, Copy, Table as TableIcon, Printer, Columns as ColumnsIcon, Funnel, ArrowsClockwise, X, PencilSimple, ChartLine, CaretDown, ArrowUp, ArrowDown, DownloadSimple, Upload, IdentificationCard, CheckCircle, XCircle, Eye, Lightning, Pause, Play, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import KickDialog from "@/components/KickDialog";

/* ---------------------------------------------------------- */
/*  User List page — SNS-style table with toolbar & exports   */
/* ---------------------------------------------------------- */

const KYC_TYPES = [
  // TRAI-compliant E-KYC set (FU 86) — required for every new signup.
  { key: "aadhaar_front", label: "Aadhaar (front)", required: true },
  { key: "aadhaar_back",  label: "Aadhaar (back)",  required: true },
  { key: "pan",           label: "PAN card",        required: true },
  { key: "selfie",        label: "Live Selfie",     required: true },
  // Legacy generic types kept for uploads made before FU 86 migration.
  { key: "id_proof",      label: "ID Proof" },
  { key: "address_proof", label: "Address Proof" },
  { key: "photo",         label: "Photo" },
  { key: "caf",           label: "CAF" },
];
const KYC_STATUS_TONE = {
  verified: { cls: "bg-[hsl(var(--success)/0.25)] text-[hsl(var(--success))] border-[hsl(var(--success))]/40", label: "verified" },
  pending:  { cls: "bg-[hsl(var(--warning)/0.25)] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40", label: "pending review" },
  rejected: { cls: "bg-destructive/25 text-destructive border-destructive/40", label: "rejected" },
  none:     { cls: "bg-muted/40 text-muted-foreground border-border", label: "not submitted" },
};
const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1] || "");
  r.onerror = rej;
  r.readAsDataURL(file);
});

const emptyForm = {
  full_name: "", email: "", phone: "", address: "",
  city: "", country: "India", pincode: "",
  // Coordinates default blank — only saved when the operator actually enters
  // them (Registration form or General tab "Use my location").
  latitude: "", longitude: "",
  plan_id: "", olt_id: "", olt_port: 1, nas_id: "",
  username: "", password: "", gstin: "", ip_address: "", mac_address: "", status: "active",
  creation_date: new Date().toISOString().slice(0, 10),
  expires_at: "",
};

const ALL_COLS = [
  { key: "id",         label: "ID",          default: true  },
  { key: "username",   label: "UserName",    default: true  },
  { key: "full_name",  label: "FullName",    default: true  },
  { key: "address",    label: "Address",     default: true  },
  { key: "phone",      label: "MobileNo",    default: true  },
  { key: "plan",       label: "Plan",        default: true  },
  { key: "signal_dbm", label: "RX dBm",      default: true  },
  { key: "expires_at", label: "ExpiryDate",  default: true  },
  { key: "operator",   label: "Operator",    default: true  },
  { key: "status",     label: "Acct.Status", default: true  },
  { key: "kyc",        label: "KYC",         default: true  },
  { key: "action",     label: "Action",      default: true  },
];

export default function Subscribers() {
  const navigate = useNavigate();
  const [subs, setSubs]     = useState([]);
  const [plans, setPlans]   = useState([]);
  const [olts, setOlts]     = useState([]);
  const [nasList, setNas]   = useState([]);
  const [q, setQ]           = useState("");
  const [searchParams]      = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("filter") || "all");
  const [pageSize, setPageSize]   = useState(10);
  const [page, setPage]           = useState(1);
  const [sort, setSort]           = useState({ col: "id", dir: "asc" });
  const [colsVisible, setColsVisible] = useState(() => Object.fromEntries(ALL_COLS.map((c) => [c.key, c.default])));
  const [colsPopupOpen, setColsPopupOpen] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState({}); // {sub_id: true}
  const csvInputRef = useRef(null);
  const [kycModal, setKycModal] = useState(null); // { subscriber }
  const [busyDisconnect, setBusyDisconnect] = useState(null); // sub_id currently being disconnected
  const [busySuspend, setBusySuspend]       = useState(null); // sub_id currently toggling suspend
  const [bulkBusy, setBulkBusy]             = useState(false);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const disconnectSub = async (s) => {
    if (!window.confirm(`Send RADIUS Disconnect-Request for ${s.username || s.full_name}?\nThis will kick the current session; the client can reconnect immediately.`)) return;
    try {
      setBusyDisconnect(s.id);
      const { data } = await http.post(`/subscribers/${s.id}/coa/disconnect`);
      const coa = data?.coa || {};
      const rc = coa.radclient_status || "?";
      const api = coa.api_status || "not_attempted";
      const detail = `CoA:${rc} · API:${api}${coa.api_output ? " · " + coa.api_output : ""}`;
      if (data?.ok) toast.success(`Disconnected ${s.username} — ${detail}`);
      else if (rc === "radclient_not_installed") toast.error("radclient not installed on backend host");
      else if (rc === "no_nas") toast.error("Subscriber has no NAS linked");
      else toast.error(`Not disconnected: ${s.username} — ${detail}`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally { setBusyDisconnect(null); }
  };

  const removeSub = async (s) => {
    if (!window.confirm(`⚠️ REMOVE ${s.username || s.full_name}?\n\nThis hard-deletes the subscriber and all their invoices + RADIUS history.\nAn archive copy is kept in \`deleted_subscribers\` for audit.\n\nContinue?`)) return;
    try {
      await http.delete(`/subscribers/${s.id}`);
      toast.success(`Removed ${s.username || s.full_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleSuspend = async (s) => {
    const suspended = s.status === "suspended";
    const verb = suspended ? "reactivate" : "suspend";
    if (!window.confirm(`${verb.toUpperCase()} ${s.username || s.full_name}?${suspended ? "" : "\nThis will disable RADIUS auth and drop the live session (CoA)."}`)) return;
    try {
      setBusySuspend(s.id);
      await http.post(`/subscribers/${s.id}/${suspended ? "activate" : "suspend"}`);
      toast.success(`${s.username || "Subscriber"} ${suspended ? "reactivated" : "suspended + disconnected"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally { setBusySuspend(null); }
  };

  const bulkDisconnect = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Send Disconnect-Request to ${selectedIds.length} selected subscriber(s)?`)) return;
    try {
      setBulkBusy(true);
      const { data } = await http.post("/subscribers/bulk/coa-disconnect", { ids: selectedIds });
      const { total, sent } = data?.summary || {};
      toast.success(`Bulk disconnect: ${sent}/${total} sent`);
      setSelected({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk disconnect failed");
    } finally { setBulkBusy(false); }
  };

  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && f !== statusFilter) setStatusFilter(f);
  }, [searchParams]); // eslint-disable-line

  const load = async () => {
    const [a, b, c, d] = await Promise.all([http.get("/subscribers"), http.get("/plans"), http.get("/olts"), http.get("/nas")]);
    setSubs(a.data); setPlans(b.data); setOlts(c.data); setNas(d.data);
  };
  useEffect(() => { load(); }, []);

  const planMap = useMemo(() => Object.fromEntries(plans.map((p) => [p.id, p])), [plans]);
  const nasMap  = useMemo(() => Object.fromEntries(nasList.map((n) => [n.id, n])), [nasList]);

  // Enrich rows with sequential numeric ID (starting 16600 for SNS-vibe), plan name and operator (NAS name)
  const enriched = useMemo(() => {
    const sortedByCreated = [...subs].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return sortedByCreated.map((s, i) => ({
      ...s,
      _numeric_id: 16600 + i,
      _plan_name: planMap[s.plan_id]?.name || "—",
      _plan_speed: planMap[s.plan_id]?.speed_mbps || 0,
      _operator: nasMap[s.nas_id]?.name || "—",
    }));
  }, [subs, planMap, nasMap]);

  const filtered = useMemo(() => {
    return enriched.filter((s) => {
      const matchQ = !q || [s.full_name, s.username, s.ip_address, s.mac_address, s.phone, s.address, s._plan_name, s._operator].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase());
      const matchS = statusFilter === "all" || s.connection_status === statusFilter || s.status === statusFilter;
      return matchQ && matchS;
    });
  }, [enriched, q, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { col, dir } = sort;
    const acc = {
      id:         (r) => r._numeric_id,
      username:   (r) => (r.username || "").toLowerCase(),
      full_name:  (r) => (r.full_name || "").toLowerCase(),
      address:    (r) => (r.address || "").toLowerCase(),
      phone:      (r) => r.phone || "",
      plan:       (r) => r._plan_name,
      expires_at: (r) => r.expires_at || "",
      operator:   (r) => r._operator,
      status:     (r) => r.connection_status || "",
    }[col] || ((r) => r._numeric_id);
    arr.sort((a, b) => {
      const va = acc(a), vb = acc(b);
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages]); // eslint-disable-line

  /* ------------ Exports ------------ */
  const exportRows = () => sorted.map((r) => ({
    ID: r._numeric_id, UserName: r.username, FullName: r.full_name, Address: r.address,
    MobileNo: r.phone, Plan: r._plan_name, ExpiryDate: r.expires_at || "",
    Operator: r._operator, "Acct.Status": r.connection_status || "",
  }));
  const copyTable = () => {
    const rows = exportRows();
    if (!rows.length) return toast.error("No rows to copy");
    const header = Object.keys(rows[0]).join("\t");
    const body = rows.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(header + "\n" + body).then(() => toast.success(`${rows.length} rows copied to clipboard`));
  };
  const exportCSV = () => {
    const rows = exportRows();
    if (!rows.length) return toast.error("No rows to export");
    const csv = [Object.keys(rows[0]).join(","), ...rows.map((r) => Object.values(r).map((v) => `"${String(v).replaceAll('"', '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `users_${Date.now()}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const printTable = () => window.print();

  /* ------------ CSV Import (kept from previous) ------------ */
  const onImportCSV = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    const fd = new FormData(); fd.append("file", f);
    const t = toast.loading("Importing CSV…");
    try {
      const { data } = await http.post("/subscribers/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.dismiss(t);
      const errCount = (data.errors || []).length;
      if (errCount === 0) toast.success(`Imported ${data.created} subscribers`);
      else toast.warning(`Imported ${data.created}, ${errCount} skipped`);
      load();
    } catch { toast.dismiss(t); toast.error("Import failed"); }
  };
  const downloadTemplate = async () => {
    const { data } = await http.get("/subscribers/bulk-import/template", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "users_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <div className="space-y-4 fade-up" data-testid="user-list-view">
      {/* Green header (SNS-style) */}
      <div className="bg-[hsl(var(--success)/0.9)] text-white px-4 py-2.5 flex items-center justify-between rounded-t-sm">
        <div className="inline-flex items-center gap-2 font-medium">
          <Users size={16} weight="fill" /> User List
        </div>
        <div className="inline-flex items-center gap-2">
          <button onClick={load} title="Reload" className="p-1 hover:bg-white/10 rounded" data-testid="reload-btn"><ArrowsClockwise size={14} /></button>
          <button title="Collapse" className="p-1 hover:bg-white/10 rounded"><CaretDown size={14} /></button>
          <button title="Close" className="p-1 hover:bg-white/10 rounded"><X size={14} /></button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border border-border bg-card px-3 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-8 w-auto text-xs px-2" data-testid="page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>Show {n} rows</SelectItem>)}</SelectContent>
          </Select>
          <ToolBtn icon={Copy} label="Copy" onClick={copyTable} testid="tool-copy" />
          <ToolBtn icon={TableIcon} label="Excel" onClick={exportCSV} testid="tool-excel" />
          <ToolBtn icon={Printer} label="Print" onClick={printTable} testid="tool-print" />
          <div className="relative">
            <ToolBtn icon={ColumnsIcon} label="Column" onClick={() => setColsPopupOpen((v) => !v)} testid="tool-column" />
            {colsPopupOpen && (
              <div className="absolute z-20 top-full mt-1 left-0 bg-card border border-border shadow-lg p-2 w-52" data-testid="cols-popup">
                {ALL_COLS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-xs py-1 cursor-pointer hover:bg-secondary/40 px-1">
                    <input type="checkbox" checked={!!colsVisible[c.key]} onChange={(e) => setColsVisible({ ...colsVisible, [c.key]: e.target.checked })} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="inline-flex items-center gap-1 border border-border rounded-sm px-2 h-8">
            <Funnel size={12} className="text-muted-foreground" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs bg-transparent focus:outline-none" data-testid="filter-select">
              <option value="all">All</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find…" data-testid="search-input" className="pl-7 h-8 w-52 text-xs" />
          </div>
          <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={onImportCSV} data-testid="csv-input" />
          <Button variant="outline" size="sm" onClick={() => csvInputRef.current?.click()} className="h-8 text-xs rounded-sm" data-testid="csv-import"><Upload size={12} className="mr-1" /> Import</Button>
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="h-8 text-xs rounded-sm" data-testid="csv-template"><DownloadSimple size={12} className="mr-1" /> Template</Button>
          {selectedIds.length > 0 && (
            <Button
              size="sm"
              onClick={bulkDisconnect}
              disabled={bulkBusy}
              data-testid="bulk-disconnect"
              className="h-8 text-xs rounded-sm bg-amber-500 hover:bg-amber-500/85 text-white"
            >
              <Lightning size={12} className="mr-1" /> {bulkBusy ? "Sending…" : `Disconnect selected (${selectedIds.length})`}
            </Button>
          )}
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild><Button size="sm" className="h-8 text-xs rounded-sm bg-primary hover:bg-primary/90" data-testid="new-sub"><Plus size={12} className="mr-1" /> New user</Button></DialogTrigger>
            <CreateSubscriberModal plans={plans} olts={olts} nasList={nasList} onCreated={() => { setOpenCreate(false); load(); }} />
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="user-table">
          <thead className="bg-[hsl(var(--success)/0.08)] border-b border-border">
            <tr>
              <th className="p-2 w-8"><input type="checkbox" onChange={(e) => { const on = e.target.checked; const map = {}; pageRows.forEach((r) => { map[r.id] = on; }); setSelected(map); }} data-testid="select-all" /></th>
              {ALL_COLS.filter((c) => colsVisible[c.key]).map((c) => (
                <th key={c.key} className="text-left py-2 px-3 text-xs font-semibold text-foreground/90 whitespace-nowrap">
                  <button onClick={() => c.key !== "action" && toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`sort-${c.key}`}>
                    {c.label}
                    {sort.col === c.key && (sort.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
                    {sort.col !== c.key && c.key !== "action" && <span className="text-muted-foreground text-[9px]">⇅</span>}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={ALL_COLS.length + 1} className="text-center py-10 text-muted-foreground text-sm">No users match your filter.</td></tr>
            )}
            {pageRows.map((s) => {
              const online = s.connection_status === "online";
              // Row-tint priority (highest visual first): Expired → Suspended → Online → Offline (plain).
              // Expired wins because a red row is the strongest "act now" signal — an expired
              // subscriber can't get past walled-garden even if they're currently connected.
              // Match BOTH server-set state (`status:"expired"` set by auto-suspend cron) and
              // client-side date check (`expires_at < now`) so every user is coloured correctly
              // regardless of whether the daily cron has run yet.
              const isExpired = s.status === "expired" || (() => {
                if (!s.expires_at) return false;
                // Live data stores expires_at in TWO formats: DD-MM-YYYY
                // (older manual entries + CSV imports) and YYYY-MM-DD (new
                // creates). JavaScript's `Date.parse("01-10-2026")` silently
                // reads that as `Jan 10 2026` — flipping every future
                // DD-MM-YYYY row where day ≤ 12 into a false-positive
                // "expired" (red) tint. Parse both shapes explicitly and
                // compare on the local calendar date, not on Date.now().
                const v = String(s.expires_at).trim().slice(0, 10);
                const mISO = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                const mDMY = v.match(/^(\d{2})-(\d{2})-(\d{4})$/);
                let y, mo, d;
                if (mISO) { [, y, mo, d] = mISO; }
                else if (mDMY) { [, d, mo, y] = mDMY; }
                else return false;
                const exp = new Date(+y, +mo - 1, +d).getTime();
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                return Number.isFinite(exp) && exp < today;
              })();
              const isSuspended = s.status === "suspended" || s.status === "blocked";
              // Screen tints; print-media overrides live in index.css keyed on data-state.
              // Opacities intentionally strong (0.30–0.40) so the row state
              // is unmistakable at a glance across a long list.
              const rowTone = isExpired
                ? "bg-[hsl(var(--destructive)/0.32)] hover:bg-[hsl(var(--destructive)/0.42)]"
                : isSuspended
                  ? "bg-[hsl(var(--warning)/0.32)] hover:bg-[hsl(var(--warning)/0.42)]"
                  : online
                    ? "bg-[hsl(var(--success)/0.28)] hover:bg-[hsl(var(--success)/0.38)]"
                    : "hover:bg-secondary/60";
              return (
                <tr key={s.id} className={`border-b border-border/50 transition-colors ${rowTone}`} data-testid={`row-${s.id}`} data-state={isExpired ? "expired" : isSuspended ? "suspended" : online ? "online" : "offline"}>
                  <td className="p-2"><input type="checkbox" checked={!!selected[s.id]} onChange={(e) => setSelected({ ...selected, [s.id]: e.target.checked })} data-testid={`select-${s.id}`} /></td>
                  {colsVisible.id         && <td className="py-2 px-3 font-mono text-xs">{s._numeric_id}</td>}
                  {colsVisible.username   && <td className="py-2 px-3"><Link to={`/users/${s.id}`} className="text-primary hover:underline font-mono text-sm" data-testid={`user-link-${s.id}`}>{s.username}</Link></td>}
                  {colsVisible.full_name  && <td className="py-2 px-3">{s.full_name}</td>}
                  {colsVisible.address    && <td className="py-2 px-3 text-xs">{s.address}</td>}
                  {colsVisible.phone      && <td className="py-2 px-3 font-mono text-xs">{s.phone}</td>}
                  {colsVisible.plan       && <td className="py-2 px-3"><Link to="/plans" className="text-primary hover:underline text-xs">{s._plan_name}</Link></td>}
                  {colsVisible.signal_dbm && (() => {
                    const rx = s.signal_dbm;
                    const plan = planMap[s.plan_id] || {};
                    let tone = "text-muted-foreground";
                    let breach = null;
                    if (typeof rx === "number") {
                      if (plan.rx_low_dbm != null && rx < plan.rx_low_dbm)  { tone = "text-[hsl(var(--destructive))]"; breach = `below ${plan.rx_low_dbm} dBm`; }
                      else if (plan.rx_high_dbm != null && rx > plan.rx_high_dbm) { tone = "text-[hsl(var(--warning))]"; breach = `above ${plan.rx_high_dbm} dBm`; }
                      else if (plan.rx_low_dbm != null || plan.rx_high_dbm != null) { tone = "text-[hsl(var(--success))]"; }
                    }
                    return (
                      <td className="py-2 px-3 font-mono text-xs" data-testid={`rx-${s.id}`}>
                        <span className={tone} title={breach || (plan.rx_low_dbm != null || plan.rx_high_dbm != null ? "within plan window" : "no plan threshold set")}>
                          {typeof rx === "number" ? `${rx.toFixed(1)}` : "—"}
                        </span>
                      </td>
                    );
                  })()}
                  {colsVisible.expires_at && <td className="py-2 px-3 font-mono text-xs">{s.expires_at || "—"}</td>}
                  {colsVisible.operator   && <td className="py-2 px-3 text-xs">{s._operator}</td>}
                  {colsVisible.status     && <td className="py-2 px-3"><span className="inline-flex items-center gap-1.5 text-xs"><span className={`pulse-dot ${s.connection_status || "offline"}`} />{online ? "Online" : "Offline"}</span></td>}
                  {colsVisible.kyc        && (() => {
                    const kyc = s.kyc || {};
                    const count = KYC_TYPES.filter((t) => kyc[t.key]).length;
                    const full = count === 4;
                    return (
                      <td className="py-2 px-3">
                        <button onClick={() => setKycModal(s)} data-testid={`kyc-btn-${s.id}`}
                                className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 border rounded-sm ${full ? "text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)]" : "text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]"}`}>
                          <IdentificationCard size={12} /> {count}/4
                        </button>
                      </td>
                    );
                  })()}
                  {colsVisible.action     && (
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <Link to={`/users/${s.id}?tab=statistics`} data-testid={`traffic-${s.id}`}
                              className="inline-flex items-center gap-1 bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))/0.85] text-white text-xs px-2.5 py-1 rounded-sm">
                          <ChartLine size={11} /> Traffic
                        </Link>
                        <Link to={`/users/${s.id}`} data-testid={`edit-${s.id}`}
                              className="inline-flex items-center gap-1 bg-primary hover:bg-primary/85 text-white text-xs px-2.5 py-1 rounded-sm">
                          <PencilSimple size={11} /> Edit
                        </Link>
                        <KickDialog subscriberId={s.id} username={s.username || s.full_name} onDone={load}>
                          <button
                            disabled={busyDisconnect === s.id}
                            data-testid={`disconnect-${s.id}`}
                            title="Kick (RADIUS Disconnect) with optional re-auth block"
                            className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-500/85 disabled:opacity-50 text-white text-xs px-2.5 py-1 rounded-sm"
                          >
                            <Lightning size={11} /> Kick
                          </button>
                        </KickDialog>
                        <button
                          onClick={() => toggleSuspend(s)}
                          disabled={busySuspend === s.id}
                          data-testid={`suspend-${s.id}`}
                          title={s.status === "suspended" ? "Reactivate — allow RADIUS auth again" : "Suspend — block RADIUS auth and drop live session"}
                          className={`inline-flex items-center gap-1 disabled:opacity-50 text-white text-xs px-2.5 py-1 rounded-sm ${s.status === "suspended" ? "bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))/0.85]" : "bg-destructive hover:bg-destructive/85"}`}
                        >
                          {s.status === "suspended" ? <Play size={11} /> : <Pause size={11} />}
                          {busySuspend === s.id ? "…" : (s.status === "suspended" ? "Activate" : "Suspend")}
                        </button>
                        <button
                          onClick={() => removeSub(s)}
                          data-testid={`remove-${s.id}`}
                          title="Hard-delete subscriber (archive to `deleted_subscribers` for audit)"
                          className="inline-flex items-center gap-1 bg-red-700 hover:bg-red-800 text-white text-xs px-2.5 py-1 rounded-sm"
                        >
                          <Trash size={11} /> Remove
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1 flex-wrap gap-3">
        <div>Showing {sorted.length === 0 ? 0 : (page - 1) * pageSize + 1} to {Math.min(page * pageSize, sorted.length)} of {sorted.length} entries {statusFilter !== "all" ? `(filtered from ${enriched.length})` : ""}</div>
        <div className="flex items-center gap-1">
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>First</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</PageBtn>
          {pageBtns(page, totalPages).map((n) => (
            <PageBtn key={n} active={n === page} onClick={() => setPage(n)}>{n}</PageBtn>
          ))}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</PageBtn>
        </div>
      </div>

      {/* Quick KYC modal (view + download without leaving the list) */}
      <Dialog open={!!kycModal} onOpenChange={(v) => !v && setKycModal(null)}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="kyc-quick-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              KYC — {kycModal?.full_name}
              {kycModal && (() => {
                const tone = KYC_STATUS_TONE[kycModal.kyc_status || "none"] || KYC_STATUS_TONE.none;
                return <span data-testid="kyc-status-badge" className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${tone.cls}`}>{tone.label}</span>;
              })()}
            </DialogTitle>
          </DialogHeader>
          {kycModal && kycModal.aadhaar_number && (
            <div className="text-xs mb-2 font-mono border border-border p-2 bg-secondary/30"><span className="text-muted-foreground">Aadhaar No:</span> {kycModal.aadhaar_number}</div>
          )}
          {kycModal && kycModal.kyc_status === "rejected" && kycModal.kyc_reject_reason && (
            <div className="text-xs mb-2 text-destructive border border-destructive/40 p-2 bg-destructive/10">Rejected: {kycModal.kyc_reject_reason}</div>
          )}
          <div className="space-y-2">
            {KYC_TYPES.map((t) => {
              const doc = (kycModal?.kyc || {})[t.key];
              const auth = () => `Bearer ${localStorage.getItem("token")}`;
              const url  = `${process.env.REACT_APP_BACKEND_URL}/api/subscribers/${kycModal?.id}/kyc/${t.key}`;
              const view = () => fetch(url, { headers: { Authorization: auth() } }).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank")).catch(() => toast.error("Load failed"));
              const dl   = () => fetch(url, { headers: { Authorization: auth() } }).then((r) => r.blob()).then((b) => {
                const o = URL.createObjectURL(b); const a = document.createElement("a"); a.href = o; a.download = doc?.filename || `${t.key}.bin`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(o), 1000);
              }).catch(() => toast.error("Download failed"));
              return (
                <div key={t.key} className="border border-border p-3 bg-secondary/30 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    {doc ? <CheckCircle size={16} className="text-[hsl(var(--success))]" /> : <XCircle size={16} className="text-muted-foreground" />}
                    <div>
                      <div className="text-sm">{t.label}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[220px]">{doc?.filename || "not uploaded"}</div>
                    </div>
                  </div>
                  {doc && (
                    <div className="flex items-center gap-2">
                      <button onClick={view} className="text-xs text-primary hover:underline font-mono inline-flex items-center gap-1" data-testid={`kyc-quick-view-${t.key}`}><Eye size={11} /> view</button>
                      <button onClick={dl} className="text-xs text-[hsl(var(--success))] hover:underline font-mono inline-flex items-center gap-1" data-testid={`kyc-quick-dl-${t.key}`}><DownloadSimple size={11} /> download</button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="text-[10px] text-muted-foreground italic pt-1">Need to upload or replace? Open the user's <b>Documents</b> tab.</div>
          </div>
          {kycModal && kycModal.kyc_status === "pending" && (
            <DialogFooter className="border-t border-border pt-3 mt-2 gap-2">
              <Button
                variant="destructive"
                size="sm"
                data-testid="kyc-reject"
                onClick={async () => {
                  const reason = prompt("Reason for rejection (shown to subscriber in portal):");
                  if (!reason || !reason.trim()) return;
                  try {
                    await http.post(`/subscribers/${kycModal.id}/kyc/review`, { action: "rejected", reason: reason.trim() });
                    toast.success("KYC rejected — subscriber will see the reason in portal");
                    setKycModal(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              ><XCircle size={12} /> Reject</Button>
              <Button
                size="sm"
                data-testid="kyc-verify"
                className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white"
                onClick={async () => {
                  if (!confirm("Mark KYC as VERIFIED? Subscriber unblocked for activation.")) return;
                  try {
                    await http.post(`/subscribers/${kycModal.id}/kyc/review`, { action: "verified" });
                    toast.success("KYC verified");
                    setKycModal(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              ><CheckCircle size={12} /> Verify</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function pageBtns(cur, total) {
  const out = new Set([1, total, cur - 1, cur, cur + 1]);
  return [...out].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
}

const ToolBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1.5 border border-border rounded-sm px-2.5 h-8 text-xs hover:bg-secondary/60 transition-colors">
    <Icon size={12} /> {label}
  </button>
);
const PageBtn = ({ children, onClick, active, disabled }) => (
  <button onClick={onClick} disabled={disabled} className={`px-2.5 h-7 border rounded-sm text-xs ${active ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary/50"} disabled:opacity-40 disabled:cursor-not-allowed`}>{children}</button>
);

/* ---------------- CREATE MODAL (kept, streamlined) ---------------- */
function CreateSubscriberModal({ plans, olts, nasList, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [kycFiles, setKycFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    const missing = KYC_TYPES.filter((t) => !kycFiles[t.key]);
    if (missing.length) return toast.error(`KYC required: upload ${missing.map((m) => m.label).join(", ")}`);
    if (!form.username || !form.password) return toast.error("PPPoE username and password are required");
    for (const t of KYC_TYPES) {
      const f = kycFiles[t.key];
      if (f && f.size > 4 * 1024 * 1024) return toast.error(`${t.label} > 4MB`);
    }
    setSubmitting(true);
    let created;
    try {
      const payload = { ...form, olt_port: Number(form.olt_port), gstin: form.gstin || null };
      // Only send coordinates when operator actually typed both — never dummy.
      if (form.latitude === "" || form.latitude == null) delete payload.latitude;
      else payload.latitude = Number(form.latitude);
      if (form.longitude === "" || form.longitude == null) delete payload.longitude;
      else payload.longitude = Number(form.longitude);
      const { data } = await http.post("/subscribers", payload);
      created = data;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
      setSubmitting(false); return;
    }
    try {
      for (const t of KYC_TYPES) {
        const f = kycFiles[t.key]; if (!f) continue;
        const data_base64 = await fileToBase64(f);
        await http.post(`/subscribers/${created.id}/kyc`, { doc_type: t.key, filename: f.name, content_type: f.type || "application/octet-stream", data_base64 });
      }
      toast.success("User provisioned with KYC");
    } catch { toast.warning("User created but some KYC files failed"); }
    finally { setSubmitting(false); onCreated(); }
  };

  return (
    <DialogContent className="bg-card border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="create-user-modal">
      <DialogHeader><DialogTitle className="font-display">Register new user</DialogTitle></DialogHeader>
      <form onSubmit={create} className="grid grid-cols-2 gap-4">
        <F label="Full name"><Input required data-testid="c-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></F>
        <F label="Email"><Input required type="email" data-testid="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
        <F label="Mobile"><Input required data-testid="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
        <F label="GSTIN"><Input data-testid="c-gstin" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></F>
        <F label="Address" span={2}><Input required data-testid="c-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
        <F label="City"><Input data-testid="c-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></F>
        <F label="Country"><Input data-testid="c-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></F>
        <F label="Pincode"><Input data-testid="c-pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></F>
        <F label="Latitude"><Input type="number" step="0.000001" data-testid="c-lat" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="optional" /></F>
        <F label="Longitude"><Input type="number" step="0.000001" data-testid="c-lng" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="optional" /></F>
        <F label="Expiry date"><Input type="date" data-testid="c-expiry" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></F>

        <div className="col-span-2 pt-2 border-t border-border"><div className="uppercase-label">PPPoE</div></div>
        <F label="Username"><Input required data-testid="c-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="font-mono" /></F>
        <F label="Password"><Input required data-testid="c-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="font-mono" /></F>

        <div className="col-span-2 pt-2 border-t border-border"><div className="uppercase-label">Service</div></div>
        <F label="Plan"><Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}><SelectTrigger data-testid="c-plan"><SelectValue placeholder="Plan" /></SelectTrigger><SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></F>
        <F label="NAS (Operator)"><Select value={form.nas_id} onValueChange={(v) => setForm({ ...form, nas_id: v })}><SelectTrigger data-testid="c-nas"><SelectValue placeholder="NAS" /></SelectTrigger><SelectContent>{nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent></Select></F>
        <F label="OLT"><Select value={form.olt_id} onValueChange={(v) => setForm({ ...form, olt_id: v })}><SelectTrigger data-testid="c-olt"><SelectValue placeholder="OLT" /></SelectTrigger><SelectContent>{olts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></F>
        <F label="OLT port"><Input type="number" min="1" data-testid="c-oltport" value={form.olt_port} onChange={(e) => setForm({ ...form, olt_port: e.target.value })} /></F>

        <div className="col-span-2 pt-2 border-t border-border"><div className="uppercase-label flex items-center gap-2"><IdentificationCard size={14} className="text-primary" /> KYC documents (all four required)</div></div>
        {KYC_TYPES.map((t) => (
          <div key={t.key} className="col-span-2 md:col-span-1 border border-border p-3 bg-secondary/30">
            <div className="flex items-center gap-2 mb-2 text-xs">
              {kycFiles[t.key] ? <CheckCircle size={14} className="text-[hsl(var(--success))]" /> : <XCircle size={14} className="text-muted-foreground" />}
              <span>{t.label}</span>
            </div>
            <input type="file" accept="image/*,.pdf" data-testid={`c-kyc-${t.key}`} onChange={(e) => setKycFiles({ ...kycFiles, [t.key]: e.target.files?.[0] })} className="text-xs" />
            {kycFiles[t.key] && <div className="text-[10px] font-mono text-muted-foreground truncate mt-1">{kycFiles[t.key].name}</div>}
          </div>
        ))}

        <DialogFooter className="col-span-2"><Button type="submit" data-testid="c-submit" disabled={submitting} className="rounded-sm bg-primary hover:bg-primary/90">{submitting ? "Creating…" : "Register user"}</Button></DialogFooter>
      </form>
    </DialogContent>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
